import React from "react";
import { Search, Filter, X, Loader2 } from "lucide-react";
import type { City } from "../../types/city";
import {
  NO_MAIN_CITY_FILTER_VALUE,
  UNCATEGORIZED_FILTER_VALUE,
} from "../../utils/constants";

interface BikeFiltersProps {
  search: string;
  onSearchChange: (search: string) => void;
  statusFilter: string;
  onStatusFilterChange: (status: string) => void;
  categoryFilter: string;
  onCategoryFilterChange: (categoryId: string) => void;
  availableCategories: Array<{ category_id: string; category_name: string }>;
  cityFilter: string;
  onCityFilterChange: (cityId: string) => void;
  availableCities: City[];
  searching?: boolean;
  showHiddenVehicles: boolean;
  onShowHiddenVehiclesChange: (value: boolean) => void;
}

export const BikeFilters: React.FC<BikeFiltersProps> = ({
  search,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  categoryFilter,
  onCategoryFilterChange,
  availableCategories,
  cityFilter,
  onCityFilterChange,
  availableCities,
  searching = false,
  showHiddenVehicles,
  onShowHiddenVehiclesChange,
}) => {
  const clearAllFilters = () => {
    onSearchChange("");
    onStatusFilterChange("");
    onCategoryFilterChange("");
    onCityFilterChange("");
    onShowHiddenVehiclesChange(false);
  };

  const hasActiveFilters =
    search ||
    statusFilter ||
    categoryFilter ||
    cityFilter ||
    showHiddenVehicles;

  return (
    <div className="bg-white p-4 rounded-lg shadow space-y-4">
      {/* Search */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          {searching ? (
            <Loader2 className="h-5 w-5 text-blue-500 animate-spin" />
          ) : (
            <Search className="h-5 w-5 text-gray-400" />
          )}
        </div>
        <input
          type="text"
          placeholder="Search by bike ID or SN..."
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          className="block w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      {/* Filters Row */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center space-x-2 pb-1">
          <Filter className="h-4 w-4 text-gray-500" />
          <span className="text-sm font-medium text-gray-700">Filters:</span>
        </div>

        {/* Status Filter */}
        <div className="flex flex-row items-center gap-2">
          <label className="block text-xs font-medium text-gray-700">
            Status
          </label>
          <select
            value={statusFilter}
            onChange={(e) => onStatusFilterChange(e.target.value)}
            className="block text-sm border border-gray-300 rounded-md py-1 px-2 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="maintenance">Maintenance</option>
          </select>
        </div>

        {/* Category Filter */}
        <div className="flex flex-row items-center gap-2">
          <label className="block text-xs font-medium text-gray-700">
            Category
          </label>
          <select
            value={categoryFilter}
            onChange={(e) => onCategoryFilterChange(e.target.value)}
            className="block text-sm border border-gray-300 rounded-md py-1 px-2 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">All Categories</option>
            <option value={UNCATEGORIZED_FILTER_VALUE}>Uncategorized</option>
            {availableCategories.map((category) => (
              <option
                key={category.category_id}
                value={String(category.category_id)}
              >
                {category.category_name}
              </option>
            ))}
          </select>
        </div>

        {/* Main City Filter */}
        <div className="flex flex-row items-center gap-2">
          <label className="block text-xs font-medium text-gray-700">
            Main City
          </label>
          <select
            value={cityFilter}
            onChange={(e) => onCityFilterChange(e.target.value)}
            className="block text-sm border border-gray-300 rounded-md py-1 px-2 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">All Cities</option>
            <option value={NO_MAIN_CITY_FILTER_VALUE}>No main city</option>
            {availableCities.map((city) => (
              <option key={city.city_id} value={city.city_id}>
                {city.city_name}
              </option>
            ))}
          </select>
        </div>

        <label className="flex items-center gap-2 cursor-pointer pb-1">
          <input
            type="checkbox"
            checked={showHiddenVehicles}
            onChange={(e) => onShowHiddenVehiclesChange(e.target.checked)}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <span className="text-xs font-medium text-gray-700">
            Show hidden vehicles
          </span>
        </label>

        {/* Clear Filters */}
        {hasActiveFilters && (
          <button
            onClick={clearAllFilters}
            className="inline-flex items-center px-3 py-1 text-xs font-medium text-gray-700 bg-gray-100 rounded-full hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
          >
            <X className="h-3 w-3 mr-1" />
            Clear all
          </button>
        )}
      </div>

      {/* Active Filters Summary */}
      {hasActiveFilters && (
        <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-200">
          {search && (
            <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
              Search: "{search}"
              <button
                onClick={() => onSearchChange("")}
                className="ml-1 text-blue-600 hover:text-blue-800"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          )}
          {statusFilter && (
            <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
              Status: {statusFilter}
              <button
                onClick={() => onStatusFilterChange("")}
                className="ml-1 text-green-600 hover:text-green-800"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          )}
          {categoryFilter && (
            <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-orange-100 text-orange-800 rounded-full">
              Category:{" "}
              {categoryFilter === UNCATEGORIZED_FILTER_VALUE
                ? "Uncategorized"
                : availableCategories.find(
                    (c) => String(c.category_id) === categoryFilter,
                  )?.category_name || categoryFilter}
              <button
                onClick={() => onCategoryFilterChange("")}
                className="ml-1 text-orange-600 hover:text-orange-800"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          )}
          {cityFilter && (
            <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 rounded-full">
              Main City:{" "}
              {cityFilter === NO_MAIN_CITY_FILTER_VALUE
                ? "No main city"
                : availableCities.find((c) => c.city_id === cityFilter)
                    ?.city_name || cityFilter}
              <button
                onClick={() => onCityFilterChange("")}
                className="ml-1 text-purple-600 hover:text-purple-800"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          )}
          {showHiddenVehicles && (
            <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded-full">
              Including hidden
              <button
                type="button"
                onClick={() => onShowHiddenVehiclesChange(false)}
                className="ml-1 text-gray-600 hover:text-gray-800"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          )}
        </div>
      )}
    </div>
  );
};
